/* eslint-disable max-len */
import { SHAPES, DIRECTION_SIDES, PATH_WEIGHTS } from '../../common/components/board/elements/ShapeModel';

function getCellBackground(isBlocked, gridColor, transparentBackground) {
  if (isBlocked) return gridColor;
  if (transparentBackground) return 'none';
  return '#ffffff';
}

function getTextFill(numberStyle, filled, activeColor) {
  if (numberStyle === 'plain') return activeColor;
  return filled ? '#ffffff' : activeColor;
}

function getShapeScale(shape) {
  const scales = {
    triangle: 0.9,
    triangle_down: 0.9,
    star: 0.9,
    pentagon: 0.85,
    hexagon: 0.85,
    octagon: 0.85,
    diamond: 0.9,
  };

  return scales[shape]
    ? `transform="scale(${scales[shape]})" transform-origin="12 12"`
    : '';
}

function renderPath(cell, x, y, size, settings) {
  const sides = DIRECTION_SIDES[cell.direction] || {};
  const strokeColor = settings.colored && cell.color ? cell.color : '#000000';
  const strokeW = PATH_WEIGHTS[settings.pathWeight] || PATH_WEIGHTS.normal;
  const lineAttrs = `stroke="${strokeColor}" stroke-width="${strokeW}" stroke-linecap="round"`;
  let lines = '';

  if (sides.top) lines += `<line x1="12" y1="0"  x2="12" y2="12" ${lineAttrs} />`;
  if (sides.bottom) lines += `<line x1="12" y1="12" x2="12" y2="24" ${lineAttrs} />`;
  if (sides.left) lines += `<line x1="0"  y1="12" x2="12" y2="12" ${lineAttrs} />`;
  if (sides.right) lines += `<line x1="12" y1="12" x2="24" y2="12" ${lineAttrs} />`;

  return `<svg x="${x}" y="${y}" width="${size}" height="${size}" viewBox="0 0 24 24">${lines}</svg>`;
}

function renderShape(cell, x, y, size, settings) {
  const activeColor = settings.colored && cell.color ? cell.color : '#000000';
  const { filled, value, shape } = cell;
  let inner = '';

  if (settings.shapeMode === 'number') {
    const fillStyle = filled ? activeColor : 'none';
    const textFill = getTextFill(settings.numberStyle, filled, activeColor);

    const backgrounds = {
      circle: `<circle cx="12" cy="12" r="10" fill="${fillStyle}" stroke="${activeColor}" stroke-width="1.5" />`,
      square: `<rect x="2" y="2" width="20" height="20" fill="${fillStyle}" stroke="${activeColor}" stroke-width="1.5" />`,
      rounded: `<rect x="2" y="2" width="20" height="20" rx="5" fill="${fillStyle}" stroke="${activeColor}" stroke-width="1.5" />`,
      plain: '',
    };

    inner = `${backgrounds[settings.numberStyle] || ''}
      <text x="12" y="16.5" text-anchor="middle" font-size="13" font-weight="bold" font-family="monospace" fill="${textFill}">${value}</text>`;
  } else {
    const shapeData = SHAPES[shape] || SHAPES.circle;
    const attrs = `fill="${filled ? activeColor : 'none'}" stroke="${activeColor}" stroke-width="${filled ? 0 : 2}"`;
    const transform = getShapeScale(shape);

    const tags = {
      circle: `<circle cx="12" cy="12" r="8" ${attrs} />`,
      rect: `<rect x="4" y="4" width="16" height="16" rx="2" ${attrs} />`,
      polygon: `<polygon points="${shapeData.points}" ${transform} ${attrs} />`,
      path: `<path d="${shapeData.d}" ${attrs} />`,
    };

    inner = tags[shapeData.tag] || tags.circle;
  }

  const iconSize = Math.round(size * 0.6);
  const offset = (size - iconSize) / 2;

  return `<svg x="${x + offset}" y="${y + offset}" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24">${inner}</svg>`;
}

function renderNumber(value, x, y, cellSize, color) {
  return `<text
    x="${x + cellSize / 2}"
    y="${y + cellSize / 2 + 7}"
    text-anchor="middle"
    font-family="sans-serif"
    font-weight="bold"
    font-size="20"
    fill="${color}"
  >${value}</text>`;
}

function renderSimpleBorder(svg, x, y, cellSize, gridColor) {
  return `${svg}<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}"
    fill="none" stroke="${gridColor}" stroke-width="1" />`;
}

function renderLineBorders(svg, activeCells, config, isCellEmpty) {
  const bW = config.borderWidth;

  activeCells.forEach(cell => {
    const x = cell.positionx * config.cellSize;
    const y = cell.positiony * config.cellSize;

    svg += `<line x1="${x + config.cellSize}" y1="${y}" x2="${x + config.cellSize}" y2="${y + config.cellSize}" stroke="${config.gridColor}" stroke-width="${bW}" />`;
    svg += `<line x1="${x}" y1="${y + config.cellSize}" x2="${x + config.cellSize}" y2="${y + config.cellSize}" stroke="${config.gridColor}" stroke-width="${bW}" />`;

    if (cell.positiony === 0 || isCellEmpty(cell.positionx, cell.positiony - 1)) {
      svg += `<line x1="${x}" y1="${y}" x2="${x + config.cellSize}" y2="${y}" stroke="${config.gridColor}" stroke-width="${bW}" />`;
    }

    if (cell.positionx === 0 || isCellEmpty(cell.positionx - 1, cell.positiony)) {
      svg += `<line x1="${x}" y1="${y}" x2="${x}" y2="${y + config.cellSize}" stroke="${config.gridColor}" stroke-width="${bW}" />`;
    }
  });

  return svg;
}

export default function generateGameSVG(table, rows, cols, settings = {}) {
  const config = {
    cellSize: 60,
    padding: 20,
    showAnswer: true,
    colored: false,
    shapeMode: 'geometric',
    numberStyle: 'circle',
    gridColor: '#000000',
    transparentBackground: false,
    simpleBorder: true,
    borderWidth: 2,
    ...settings,
  };

  const svgWidth = cols * config.cellSize + config.padding * 2;
  const svgHeight = rows * config.cellSize + config.padding * 2;

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
    <g transform="translate(${config.padding}, ${config.padding})">`;

  const isCellEmpty = (px, py) => {
    const neighbor = table.find(c => c.positionx === px && c.positiony === py);
    return !neighbor || neighbor.isEmpty;
  };

  const activeCells = table.filter(cell => !cell.isEmpty);

  activeCells.forEach(cell => {
    const x = cell.positionx * config.cellSize;
    const y = cell.positiony * config.cellSize;
    const cellBg = getCellBackground(cell.isBlocked, config.gridColor, config.transparentBackground);

    svg += `<rect x="${x}" y="${y}" width="${config.cellSize}" height="${config.cellSize}" fill="${cellBg}" />`;

    if (config.simpleBorder) {
      svg = renderSimpleBorder(svg, x, y, config.cellSize, config.gridColor);
    }
  });

  if (!config.simpleBorder) {
    svg = renderLineBorders(svg, activeCells, config, isCellEmpty);
  }

  activeCells.forEach(cell => {
    const x = cell.positionx * config.cellSize;
    const y = cell.positiony * config.cellSize;

    if (cell.isBlocked) {
      if (cell.value) {
        svg += renderNumber(cell.value, x, y, config.cellSize, '#ffffff');
      }
      return;
    }

    const isAnchor = cell.shape !== null;
    const hasPath = config.showAnswer && cell.direction !== null;

    if (isAnchor) {
      svg += renderShape(cell, x, y, config.cellSize, config);
    } else if (cell.isResult || (cell.value && config.showAnswer)) {
      svg += renderNumber(cell.value, x, y, config.cellSize, config.gridColor);
    }

    if (hasPath) {
      svg += renderPath(cell, x, y, config.cellSize, config);
    }
  });

  svg += '</g></svg>';
  return svg;
}
